import { useEffect, useRef, useState } from "react";
import { uploadDonors } from "../../../api/UploadDonors";
import { getDonors } from "../../../api/GetDonors";
import { useNavigate } from "react-router-dom";
import DonorTable from "./DonorTable/DonorTable";
import { errorHandler } from "../../shared/Utils/ErrorHandler";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faUpload, faCheckCircle, faTimesCircle, faTimes } from "@fortawesome/free-solid-svg-icons";

const S3_URL = "https://sap-app-staging.s3.us-west-2.amazonaws.com/resources/new_donor_list.csv";

const PAGE_SIZE = 50;

function DonorList({ onUploadSuccess }) {
  const [localUserData, setLocalUserData] = useState(null);
  const [donorData, setDonorData] = useState(null);
  const [successToast, setSuccessToast] = useState(false);
  const [errorToast, setErrorToast] = useState(false);
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const navigate = useNavigate();
  const fileInputRef = useRef();

  useEffect(() => {
    const userDataString = localStorage.getItem("userData");
    if (userDataString) {
      try {
        setLocalUserData(JSON.parse(userDataString));
      } catch {
        localStorage.removeItem("userData");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("accessToken");
        navigate("/");
      }
    }
  }, [navigate]);

  useEffect(() => {
    if (localUserData) {
      getDonors(localUserData.nonprofit, page)
        .then((res) => {
          setDonorData(res?.results || []);
          setTotalCount(res?.count || 0);
          setPageCount(Math.ceil((res?.count || 0) / PAGE_SIZE));
        })
        .catch((err) => console.error("Error getting donors:", err));
    }
  }, [localUserData, page]);

  const uploadDonorList = async (e) => {
    const csvfile = e.target.files[0];
    if (!csvfile) return;
    if (csvfile.type !== "text/csv") {
      setErrorToast(true);
      return;
    }
    const formData = new FormData();
    formData.append("file", csvfile);
    try {
      await uploadDonors(formData, localUserData.nonprofit);
      setSuccessToast(true);
      if (onUploadSuccess) onUploadSuccess();
      const updated = await getDonors(localUserData.nonprofit, 1);
      setPage(1);
      setDonorData(updated?.results || []);
      setTotalCount(updated?.count || 0);
      setPageCount(Math.ceil((updated?.count || 0) / PAGE_SIZE));
    } catch (error) {
      console.error("Error uploading file:", error);
      setErrorToast(true);
    }
    e.target.value = "";
  };

  const isAdminOrEditor =
    localUserData?.np_access_level === "admin" ||
    localUserData?.np_access_level === "editor";

  return (
    <div className="flex-1 bg-gray-100 min-h-screen p-8">
      {/* Toasts */}
      {successToast && (
        <div className="flex items-start gap-4 bg-white border border-gray-200 rounded-2xl shadow-md p-4 mb-4">
          <div className="w-10 h-10 rounded-full bg-blue flex items-center justify-center flex-shrink-0">
            <FontAwesomeIcon icon={faCheckCircle} className="text-white" />
          </div>
          <div className="flex-1 text-left">
            <p className="!text-sm !font-bold text-gray-900">Successfully Uploaded</p>
            <p className="!text-sm text-gray-500">Your CSV file was successfully uploaded.</p>
          </div>
          <button onClick={() => setSuccessToast(false)} className="text-gray-400 hover:text-gray-600">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      )}
      {errorToast && (
        <div className="flex items-start gap-4 bg-white border border-gray-200 rounded-2xl shadow-md p-4 mb-4">
          <div className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0">
            <FontAwesomeIcon icon={faTimesCircle} className="text-white" />
          </div>
          <div className="flex-1 text-left">
            <p className="!text-sm !font-bold text-gray-900">Error</p>
            <p className="!text-sm text-gray-500">Your CSV file was unable to upload.</p>
          </div>
          <button onClick={() => setErrorToast(false)} className="text-gray-400 hover:text-gray-600">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <div>
          <h1 className="!text-[30px] !font-bold !font-poppins text-blue-navy text-left">Donor List</h1>
          <p className="!text-sm text-gray-500 mt-1 text-left max-w-2xl">
            Download our CSV donor list template or upload your own. After you have successfully uploaded, make sure to refresh the page to view your donor list on the table below.
          </p>
        </div>
        {isAdminOrEditor && (
          <div className="flex items-center gap-3 flex-shrink-0 ml-6">
            <button
              onClick={() => { window.location.href = S3_URL; }}
              className="flex items-center gap-2 bg-blue-navy text-white px-4 py-2.5 rounded-full text-sm font-semibold hover:opacity-90 whitespace-nowrap"
            >
              <FontAwesomeIcon icon={faDownload} />
              Download CSV Template
            </button>
            <button
              onClick={() => fileInputRef.current.click()}
              className="flex items-center gap-2 bg-blue text-white px-4 py-2.5 rounded-full text-sm font-semibold hover:opacity-90 whitespace-nowrap"
            >
              <FontAwesomeIcon icon={faUpload} />
              Upload Donor List
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              hidden
              onChange={uploadDonorList}
            />
          </div>
        )}
      </div>

      {/* Table card */}
      <div className="bg-white rounded-2xl shadow-md mt-6 overflow-hidden">
        <DonorTable
          nonprofitId={localUserData?.nonprofit}
          data={donorData || []}
          serverPagination={true}
          serverPageCount={pageCount}
          serverTotalCount={totalCount}
          onServerPageChange={setPage}
        />
      </div>
    </div>
  );
}

export default DonorList;
